import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import type { Organization } from '../../../types';
import organizationService from '../../../services/organizationService';
import notify from '@/lib/notify';
import { useAuthStore } from '@/stores/authStore';
import { useOrgUiSettingsStore } from '@/stores/orgUiSettingsStore';
import { apiErrorDetail } from '@/lib/knowledgeSharing';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Common/Card';
import { Switch } from '@/components/Common/switch';

interface KnowledgeSharingCardProps {
  organization: Organization;
  canEdit: boolean;
}

/** Org setting `knowledge_private_by_default`: new documents and folders start private. Saved on toggle. */
export const KnowledgeSharingCard: React.FC<KnowledgeSharingCardProps> = ({ organization, canEdit }) => {
  const { t } = useTranslation();
  // Same org as the one useLoadOrgUiSettings puts in the store.
  const currentOrgId = useAuthStore((s) => s.user?.organization_id ?? null);
  const setOrgUiSettings = useOrgUiSettingsStore((s) => s.setSettings);

  const [privateByDefault, setPrivateByDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Follows changes made elsewhere on the page (e.g. the Olisé preset) for the active org.
  const storeValue = useOrgUiSettingsStore((s) =>
    organization.id === currentOrgId ? s.settings?.knowledge_private_by_default : undefined
  );

  useEffect(() => {
    if (storeValue !== undefined) setPrivateByDefault(storeValue);
  }, [storeValue]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    organizationService
      .getUiSettings(organization.id)
      .then((settings) => {
        if (!cancelled) setPrivateByDefault(settings.knowledge_private_by_default);
      })
      .catch((error: unknown) => {
        console.error('Error loading organization UI settings:', error);
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organization.id]);

  const handleChange = async (checked: boolean) => {
    setSaving(true);
    try {
      const updated = await organizationService.updateUiSettings(organization.id, {
        knowledge_private_by_default: checked,
      });
      setPrivateByDefault(updated.knowledge_private_by_default);
      if (organization.id === currentOrgId) setOrgUiSettings(updated);
      notify.success(t('knowledgeSharing.settings.saved'));
    } catch (error) {
      console.error('Error updating knowledge sharing setting:', error);
      notify.error(apiErrorDetail(error) ?? t('knowledgeSharing.settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('knowledgeSharing.settings.title')}</CardTitle>
        <CardDescription>{t('knowledgeSharing.settings.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        ) : loadError ? (
          <p className="text-sm text-destructive">{t('knowledgeSharing.settings.loadError')}</p>
        ) : (
          <label className="flex items-center gap-3 text-sm">
            <Switch
              checked={privateByDefault}
              onCheckedChange={(checked) => void handleChange(checked)}
              disabled={!canEdit || saving}
              aria-busy={saving}
            />
            <span>{t('knowledgeSharing.settings.privateByDefault')}</span>
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </label>
        )}
      </CardContent>
    </Card>
  );
};

export default KnowledgeSharingCard;
